import { BaseService } from "./base.service";
import { IProgrammeByErpDBCompany } from "models/ProgrammeModel";
import { IProgrammeList } from "models/Programme/ProgrammeListModel";
import { TableFilterModel } from "models/TableFilterModel";
import { IProgrammeGridDetail } from "models/Programme/ProgrammeGridDetailModel";
import { IProgrammeStatus } from "models/Programme/ProgrammeStatusModel";
import ProgrammeDetailModel, {
	IDepartmentByProgrammeId,
} from "models/Programme/ProgrammeDetailModel";
import { KeyFilterModel } from "models/SystemScheduler/SystemSchedulerFilterModel";
import { IProgrammeDeptByErpDbCompany } from "models/Budget/BudgetAllocationDetail";
import {
	ISubDepartmentOrganizationStructure,
	ISubDepartmentUser,
} from "models/SubDepartmentModel";

class ProgrammeServiceInternal extends BaseService {
	getProgrammeByErpDbCompanyId = async (
		erpDbCompanyIds: string,
		alreadySelectedProgrammeId: number = 0,
		isStrategicInitiative: boolean | null = null
	): Promise<IProgrammeByErpDBCompany[]> => {
		let queryParams = new URLSearchParams({
			alreadySelectedProgrammeId: alreadySelectedProgrammeId.toString(),
		});
		if (isStrategicInitiative !== null) {
			queryParams.set(
				"isStrategicInitiative",
				isStrategicInitiative.toString()
			);
		}
		return this.callApi<IProgrammeByErpDBCompany[]>(
			`Programme/GetProgrammeByErpDbCompanyId/${erpDbCompanyIds}`,
			"GET",
			queryParams
		);
	};

	getAllProgrammeList = async (statusIds = ""): Promise<IProgrammeList[]> =>
		this.callApi<IProgrammeList[]>(
			`Programme/GetProgrammeList`,
			"GET",
			new URLSearchParams({
				statusIds: statusIds,
			})
		);

	getProgrammeStatusList = async (): Promise<IProgrammeStatus[]> =>
		this.callApi<IProgrammeStatus[]>(
			`ProgrammeStatus/GetProgrammeStatusList`,
			"GET"
		);

	getProgrammeGridList = async (
		model: TableFilterModel
	): Promise<IProgrammeGridDetail[]> =>
		this.callApi<IProgrammeGridDetail[]>(
			`Programme/GetAllProgrammeList`,
			"POST",
			undefined,
			model
		);

	getProgrammeDetail = async (
		programmeId: number
	): Promise<ProgrammeDetailModel> =>
		this.callApi<ProgrammeDetailModel>(
			`Programme/GetProgrammeData/${programmeId}`,
			"GET"
		);

	saveProgrammeDetail = async (
		model: ProgrammeDetailModel
	): Promise<{ programmeId: number; programmeNumber: string }> =>
		this.callApi<{ programmeId: number; programmeNumber: string }>(
			`Programme/Save`,
			"POST",
			undefined,
			model
		);

	updateProgrammeDetail = async (
		model: ProgrammeDetailModel
	): Promise<undefined> =>
		this.callApi<undefined>(
			`Programme/Update/${model.programmeId}`,
			"PUT",
			undefined,
			model
		);

	deleteProgrammeDetail = async (programmeId: number): Promise<undefined> =>
		this.callApi<undefined>(
			`Programme/Delete/${programmeId}`,
			"DELETE",
			undefined,
			undefined,
			true
		);

	updateProgrammeStatus = async (
		programmeId: number,
		isActivate: boolean
	): Promise<number> =>
		this.callApi<number>(
			`Programme/UpdateStatus/${programmeId}/${isActivate}`,
			"PUT"
		);

	getProgrammeDepartmentList = async (
		model: KeyFilterModel
	): Promise<IDepartmentByProgrammeId[]> =>
		this.callApi<IDepartmentByProgrammeId[]>(
			`ProgrammeDepartment/GetProgrammeDepartmentList`,
			"POST",
			undefined,
			model
		);

	deleteProgrammeDepartment = async (
		programmeDepartmentId: number
	): Promise<boolean> =>
		this.callApi<boolean>(
			`ProgrammeDepartment/Delete/${programmeDepartmentId}`,
			"DELETE"
		);

	checkProgrammeAlreadyUsed = async (programmeId: number): Promise<boolean> =>
		this.callApi<boolean>(
			`Programme/CheckProgrammeAlreadyUsed/${programmeId}`,
			"GET"
		);

	getProgrammeDepartmentListByErpDbCompanyId = async (
		erpDbCompanyId: number,
		financialYearId: number
	): Promise<IProgrammeDeptByErpDbCompany[]> =>
		this.callApi<IProgrammeDeptByErpDbCompany[]>(
			`Programme/GetProgrammeDepartmentListByErpDbCompanyId/${erpDbCompanyId}/${financialYearId}`,
			"GET"
		);

	getProgrammesByContractId = async (
		contractId: number
	): Promise<IProgrammeByErpDBCompany[]> =>
		this.callApi<IProgrammeByErpDBCompany[]>(
			`ContractLine/GetProgrammesByContractId/${contractId}`,
			"GET"
		);

	deleteProgrammeEmployee = async (
		programmeOrganizationStructureUserId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`Programme/DeleteUser/${programmeOrganizationStructureUserId}`,
			"DELETE",
			undefined,
			undefined,
			true
		);

	saveProgrammeAttachment = async (model: FormData): Promise<number> =>
		this.callApi<number>(
			`ProgrammeAttachment/Save`,
			"POST",
			undefined,
			model,
			false,
			true
		);

	updateProgrammeAttachment = async (
		programmeAttachmentId: number,
		model: FormData
	): Promise<number> =>
		this.callApi<number>(
			`ProgrammeAttachment/Update/${programmeAttachmentId}`,
			"PUT",
			undefined,
			model,
			false,
			true
		);

	getProgrammeAttachment = async (programmeId: number): Promise<any[]> =>
		this.callApi<any[]>(
			`ProgrammeAttachment/GetProgrammeAttachment/${programmeId}`,
			"GET"
		);

	deleteProgrammeAttachment = async (
		programmeAttachmentId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`ProgrammeAttachment/Delete/${programmeAttachmentId}`,
			"DELETE",
			undefined,
			undefined,
			true
		);

	getUserList = async (
		payload: KeyFilterModel
	): Promise<ISubDepartmentUser[]> =>
		this.callApi<ISubDepartmentUser[]>(
			`Programme/GetUserList`,
			"POST",
			undefined,
			payload
		);

	GetProgrammeOrganizationStructureList = async (
		payload: KeyFilterModel
	): Promise<ISubDepartmentOrganizationStructure[]> =>
		this.callApi<ISubDepartmentOrganizationStructure[]>(
			`Programme/GetProgrammeOrganizationStructureList`,
			"POST",
			undefined,
			payload
		);

	deleteProjectLink = async (
		programmeId: number,
		projectId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`Programme/DeleteProjectMapping/${programmeId}/${projectId}`,
			"DELETE",
			undefined,
			undefined,
			true
		);
}

export const ProgrammeService = new ProgrammeServiceInternal();
